import './OverallDealStructureSubTab.scss';
import React, { useMemo } from 'react';
import { ConsiderationLedger } from '../../../../../components/FlowOfFunds/ConsiderationLedger/ConsiderationLedger';
import {
    ConsiderationTableItem,
    TypesOfConsiderationTable,
} from '../../../LoiComparisonTab/LoiComparisonSummary/TypesOfConsiderationTable';
import { TypeOfConsideration } from '../../../../../slices/marketplace/pricing/models/type-of-consideration.enum';
import { ConsiderationFeeInputs } from '../../../../../models/FlowOfFunds/flow-of-funds.model';
import { useFlowOfFunds } from '../useFlowOfFunds';

export interface OverallDealStructureSubTabProps {
    engagementId: number;
}

const buildConsiderationItem = (
    feeInputs: ConsiderationFeeInputs,
    winningBuyerId: number
): ConsiderationTableItem => {
    const fairValueByType: Partial<Record<TypeOfConsideration, number | null>> = {};
    const faceValueByType: Partial<Record<TypeOfConsideration, number | null>> = {};

    (feeInputs.rows ?? []).forEach((row) => {
        fairValueByType[row.considerationType] = row.fairValue;
        faceValueByType[row.considerationType] = row.faceValue;
    });

    return {
        buyerId: winningBuyerId,
        buyerName: '',
        considerationRows: feeInputs.considerationRows ?? [],
        fairValueByType,
        faceValueByType,
    };
};

/** Purchase Price Consideration — the consideration summary ledger plus the
 *  per-type Consideration Detail tables. */
export const OverallDealStructureSubTab = (props: OverallDealStructureSubTabProps) => {
    const { flowOfFunds, isLoading } = useFlowOfFunds(props.engagementId);

    const feeInputs = flowOfFunds?.consideration?.feeInputs;
    const considerationItems = useMemo(
        () =>
            feeInputs
                ? [buildConsiderationItem(feeInputs, flowOfFunds.winningBuyerId)]
                : [],
        [feeInputs, flowOfFunds?.winningBuyerId]
    );

    if (isLoading) {
        return <div className="mt-4 text-muted">Loading…</div>;
    }
    if (!flowOfFunds?.consideration) {
        return (
            <div className="mt-4 text-muted">
                No consideration has been recorded for this engagement yet.
            </div>
        );
    }

    return (
        <div className="mt-4 overall-deal-structure">
            <ConsiderationLedger consideration={flowOfFunds.consideration} />
            {considerationItems.length > 0 && (
                <div className="mt-4 overall-deal-structure__consideration-types">
                    <TypesOfConsiderationTable
                        items={considerationItems}
                        canEditTiming={false}
                        canEditCashFlow={false}
                        canEditComment={false}
                        levelOfService={feeInputs.levelOfService}
                        onTimingChange={() => undefined}
                        onCashFlowChange={() => undefined}
                        onCommentChange={() => undefined}
                        onBlurSave={() => undefined}
                        fullWidth
                    />
                </div>
            )}
        </div>
    );
};

export default OverallDealStructureSubTab;
